'use client';

import clsx from 'clsx';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGSAP } from '@gsap/react';
import initAnimation from './animations/case-testing-data-animation';
import { useLocale } from '@/components/providers/context/locale-context';

type TestDataCardInfo = {
    title: string;
    bgUrl: string;
    tableTitles: string[];
    tableDatas: string[][];
    comments: string[];
};

const TestingDataCard = ({
    info,
}: Readonly<{
    info: TestDataCardInfo;
}>) => {
    const locale = useLocale();
    return (
        <div className="flex w-full flex-col items-center justify-center overflow-hidden px-[16px] sm:px-[40px]">
            <div
                id="card-container"
                style={{
                    backgroundImage: `url(${info.bgUrl})`,
                }}
                className="flex w-full flex-col items-center gap-[24px] rounded-[12px] bg-cover bg-center bg-no-repeat py-[24px] sm:gap-[56px] sm:py-[60px]"
            >
                <h4
                    className={clsx(
                        'j-h4 w-full px-[24px] text-center text-[#FFFFFF] sm:w-3/5',
                    )}
                >
                    {info.title}
                </h4>
                <div className="flex w-full flex-col gap-[20px] px-0 sm:flex-row sm:items-stretch sm:gap-[32px] sm:px-[40px]">
                    <div id="data-table" className="w-full sm:basis-3/4 ">
                        <div
                            style={{
                                background:
                                    'linear-gradient(90deg, rgba(88, 165, 255, 0.08) 0%, rgba(255, 255, 255, 0.0232) 100%)',
                            }}
                            className="mx-[20px] overflow-x-auto overflow-y-hidden rounded-[12px] border border-[#7E7E7E] px-[23px] py-[20px] backdrop-blur-md sm:mx-0 sm:px-[32px]"
                        >
                            <table className="w-full min-w-[640px] table-auto">
                                <thead>
                                    <tr>
                                        {info.tableTitles.map(
                                            (title, index) => {
                                                const key = `testing-data-card-table-title-${index}`;
                                                return (
                                                    <th
                                                        key={key}
                                                        className={clsx(
                                                            'j-t3 whitespace-nowrap border-b-[1px] border-[#7E7E7E] text-[#C0C0C0]',
                                                        )}
                                                    >
                                                        <div className="w-full text-justify">
                                                            {title}
                                                        </div>
                                                    </th>
                                                );
                                            },
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {info.tableDatas.map((data, rowIndex) => {
                                        const key = `testing-data-card-table-row-${rowIndex}`;
                                        return (
                                            <tr key={key}>
                                                {data.map((d, index) => {
                                                    const key = `testing-data-card-table-row-${rowIndex}-${index}`;
                                                    return (
                                                        <td
                                                            key={key}
                                                            className={clsx(
                                                                'j-t2 border-[#7E7E7E] py-[12px] align-top text-[#FFFFFF]',
                                                                {
                                                                    'border-b-[1px]':
                                                                        rowIndex <
                                                                        info
                                                                            .tableDatas
                                                                            .length -
                                                                            1,
                                                                    'pr-[32px]':
                                                                        index !=
                                                                        0,
                                                                },
                                                            )}
                                                        >
                                                            {d}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div
                        id="comment-container"
                        className="w-full px-[20px] sm:basis-1/4 sm:px-0 "
                    >
                        <div className="flex h-full flex-col rounded-[12px] border border-[#7E7E7E] p-[12px] backdrop-blur-md">
                            {info.comments.map((comment, index) => {
                                const key = `testing-data-card-comment-${index}`;
                                return (
                                    <div
                                        key={key}
                                        className={clsx(
                                            ' j-t3 border-[#FFFFFF33] pb-[12px] text-[#FFFFFF]',
                                            {
                                                'pt-[12px]': index != 0,
                                                'border-b-[1px]':
                                                    index !=
                                                    info.comments.length - 1,
                                            },
                                        )}
                                    >
                                        {comment}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

function TestingData({ locale }: Readonly<{ locale: string }>) {
    const { t } = useTranslation('case');

    const testingDataList: TestDataCardInfo[] = t('testingDataList', {
        returnObjects: true,
    }) as TestDataCardInfo[];

    const rootRef = useRef<HTMLDivElement>(null);

    useGSAP(
        () => {
            return initAnimation(rootRef.current!);
        },
        { scope: rootRef },
    );

    return (
        <div
            ref={rootRef}
            className="flex w-full flex-col gap-[60px] pt-[20px] sm:pt-[100px]"
        >
            {testingDataList.map((testingDataItem, index) => {
                const key = `testing-data-card-${index}`;
                return (
                    <section
                        key={key}
                        className={`card relative w-full`}
                        style={{
                            zIndex: index * 10,
                        }}
                    >
                        <TestingDataCard info={testingDataItem} />
                    </section>
                );
            })}
        </div>
    );
}

export default TestingData;
